import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { GoogleAnalyticsService } from '@frontendlib/common/services/google-analytics.service';
import { NotificationsService } from '@modules/notification/services/notifications.service';
import { EMPTY, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

const STITCH_FAVICON = /(Stitch\.ai_Favicon_\d+)px(?:-dot)?\.png/;

/** Shows on the browser tab that notifications are waiting: a dotted favicon and a "(N) " title prefix. */
@Injectable({
  providedIn: 'root',
})
export class TabNotificationIndicatorService {
  private readonly document = inject(DOCUMENT);
  private readonly notificationsService = inject(NotificationsService);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly googleAnalytics = inject(GoogleAnalyticsService);
  private count = 0;
  /** The "(N)" this service last put in front of the title; empty while it shows none. */
  private writtenPrefix = '';
  private reportedTitle: string | undefined;

  public constructor() {
    this.keepTitlePrefix();
    this.applyTitle();
    this.currentUserStore
      .accountId$()
      .pipe(
        map(Boolean),
        distinctUntilChanged(),
        switchMap((signedIn) =>
          signedIn ? this.notificationsService.getCount().pipe(catchError(() => EMPTY)) : of(0),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((count) => {
        this.count = count;
        this.applyFavicon();
        this.applyTitle();
      });
  }

  /** Other code sets `document.title` freely (e.g. the pop-out window), which would drop the prefix. */
  private keepTitlePrefix(): void {
    const titleElement = this.document.querySelector('title');
    if (!titleElement) {
      return;
    }
    const observer = new MutationObserver(() => this.applyTitle());
    observer.observe(titleElement, { childList: true, characterData: true, subtree: true });
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  private applyFavicon(): void {
    const suffix = this.count > 0 ? '-dot' : '';
    this.document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach((link) => {
      const current = link.getAttribute('href');
      if (!current || !STITCH_FAVICON.test(current)) {
        return;
      }
      const next = current.replace(STITCH_FAVICON, `$1px${suffix}.png`);
      if (next !== current) {
        link.setAttribute('href', next);
      }
    });
  }

  /**
   * Strips only the prefix this service wrote, so a title that itself starts with "(digits)" stays intact.
   * Writes only when the title differs, so the observer's call after our own write is a no-op.
   * Analytics gets the title without the prefix.
   */
  private applyTitle(): void {
    const current = this.document.title;
    const base = this.stripWrittenPrefix(current);
    this.writtenPrefix = this.count > 0 ? `(${this.count})` : '';
    // `document.title` trims, so with an empty base the prefix is written without its trailing space.
    const next = this.writtenPrefix && base ? `${this.writtenPrefix} ${base}` : this.writtenPrefix || base;
    if (next !== current) {
      this.document.title = next;
    }
    if (base !== this.reportedTitle) {
      this.reportedTitle = base;
      this.googleAnalytics.setPageTitle(base);
    }
  }

  private stripWrittenPrefix(title: string): string {
    if (!this.writtenPrefix) {
      return title;
    }
    if (title === this.writtenPrefix) {
      return '';
    }
    return title.startsWith(`${this.writtenPrefix} `) ? title.slice(this.writtenPrefix.length + 1) : title;
  }
}
